import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BookOpenIcon } from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { type TInstructionsModalChapter } from "@/zapi-frontend/shared/ui/InstructionsModal/InstructionsModal";
import { InstructionsDialog } from "@/zapi-frontend/shared/ui/InstructionsModal/InstructionsDialog";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";

export function CaseFrameInstructions() {
  const { t } = useTranslation("projectHome");
  const { selectedProject, updateProject } = useProjectStore();
  const { isLabs } = useMeStore();

  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [projectInstructions, setProjectInstructions] = useState("");
  const [chaptersInstructions, setChaptersInstructions] = useState<Record<string, string>>({});
  const [presentationPrompt, setPresentationPrompt] = useState("");
  const isTemplateLess = selectedProject?.projectTemplateSlug === "";

  const filteredChapters = useMemo(() => {
    if (!selectedProject?.projectChapters || isTemplateLess) return [];
    return selectedProject.projectChapters.filter((chapter) => {
      if (chapter.tag === "iq_generated_appendix") return false;
      if (!isLabs && chapter.isHidden) return false;
      return true;
    });
  }, [selectedProject?.projectChapters, isLabs, isTemplateLess]);

  const chaptersForModal = useMemo<TInstructionsModalChapter[]>(
    () =>
      filteredChapters.map((chapter) => ({
        slug: chapter.projectTemplateChapter.slug,
        name: chapter.projectTemplateChapter.name,
        icon: chapter.projectTemplateChapter.icon
      })),
    [filteredChapters]
  );

  const handleOpen = () => {
    setProjectInstructions(selectedProject?.projectInstructions ?? "");
    setPresentationPrompt(selectedProject?.presentationPrompt ?? "");
    setChaptersInstructions(
      filteredChapters.reduce<Record<string, string>>((acc, chapter) => {
        acc[chapter.projectTemplateChapter.slug] = chapter.chapterInstructions ?? "";
        return acc;
      }, {})
    );
    setIsOpen(true);
  };

  if (!isLabs) return null;

  const handleSave = async () => {
    if (!selectedProject?.id || isSaving) return;
    setIsSaving(true);
    try {
      await updateProject(String(selectedProject.id), {
        projectInstructions,
        ...(isTemplateLess ? {} : { chaptersInstructions }),
        presentationPrompt
      });
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to save instructions:", error);
      toast.error(t("caseFrame.instructionsSaveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        data-testid="case-frame-instructions-button"
        title={t("projectSetup.instructions")}
        onClick={handleOpen}
      >
        <BookOpenIcon size={18} />
      </Button>

      <InstructionsDialog
        open={isOpen}
        onClose={() => {
          if (!isSaving) setIsOpen(false);
        }}
        onSave={() => void handleSave()}
        isSaving={isSaving}
        chapters={chaptersForModal}
        projectInstructions={projectInstructions}
        chaptersInstructions={chaptersInstructions}
        presentationPrompt={presentationPrompt}
        onProjectInstructionsChange={(nextProject, nextChapters) => {
          setProjectInstructions(nextProject);
          setChaptersInstructions(nextChapters);
        }}
        onPresentationPromptChange={setPresentationPrompt}
      />
    </>
  );
}
